import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SkipLink from "@/components/SkipLink";
import SectionHeader from "@/components/SectionHeader";
import CTAButton from "@/components/CTAButton";
import { StructuredData, SEO } from "@/components/SEO";
import { CheckCircle2, ArrowRight } from "lucide-react";

const plans = [
  {
    name: "Starter",
    price: "₹1,499 / month",
    description: "Perfect for beginners or those looking to maintain their fitness routine with structured guidance.",
    features: [
      "Unlimited access to group park classes",
      "1 nutrition consultation per month",
      "Basic progress tracking & analytics",
      "Access to workout library",
      "Community support & challenges",
      "Email support",
    ],
    bestFor: "Beginners, casual exercisers, budget-conscious users",
  },
  {
    name: "Pro",
    price: "₹3,999 / month",
    description: "Ideal for serious fitness enthusiasts who want personalized coaching and accelerated results.",
    features: [
      "4 personal training sessions per month",
      "2 nutrition consultations per month",
      "Full app analytics & detailed progress reports",
      "Unlimited park class access",
      "Custom workout programming",
      "Priority booking for classes",
      "24/7 chat support",
      "Monthly goal review sessions",
    ],
    highlighted: true,
    bestFor: "Regular exercisers, goal-oriented individuals, those seeking faster results",
  },
  {
    name: "Elite",
    price: "₹7,999 / month",
    description: "Comprehensive support for ambitious goals with maximum personalization and priority access.",
    features: [
      "8 personal training sessions per month",
      "Weekly nutrition check-ins & plan adjustments",
      "Priority support & custom programming",
      "Unlimited park class access with priority booking",
      "Advanced progress tracking & body composition analysis",
      "Dedicated account manager",
      "24/7 priority support",
      "Quarterly goal strategy sessions",
      "Access to exclusive workshops & events",
    ],
    bestFor: "Serious athletes, transformation goals, maximum accountability seekers",
  },
];

export default function PricingPage() {
  const breadcrumbData = {
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://fiteasy.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Pricing",
        item: "https://fiteasy.com/pricing",
      },
    ],
  };

  return (
    <>
      <SEO
        title="Pricing"
        description="View sample FitEasy pricing tiers for personal training, nutrition, and park classes. Simple plans for every fitness level. Actual pricing may vary by city and professional."
        path="/pricing"
      />
      <StructuredData type="BreadcrumbList" data={breadcrumbData} />
      <div className="bg-gradient-to-b from-slate-50 to-blue-50 text-gray-900 min-h-screen">
        <SkipLink />
        <Header />

        <main id="main-content" className="mx-auto max-w-6xl px-4 pb-16 pt-10 sm:px-6 lg:px-8">
          <SectionHeader
            id="pricing-heading"
            label="Pricing"
            title="Transparent pricing designed for every fitness journey"
            description="We believe professional fitness guidance should be accessible to everyone. Our flexible pricing plans are designed to fit different budgets and goals. Whether you're just starting your fitness journey or looking for intensive coaching, we have a plan that works for you. Note: Exact pricing may vary by city, specific trainers, and current promotional offers available in the app."
            className="mb-10 text-center"
          />

          <section aria-labelledby="pricing-heading" className="grid gap-6 md:grid-cols-3">
            {plans.map((plan) => (
              <article
                key={plan.name}
                className={`flex flex-col rounded-3xl border bg-white/90 p-6 shadow-[0_18px_60px_rgba(15,23,42,0.06)] transition hover:shadow-[0_18px_60px_rgba(15,23,42,0.12)] focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 ${
                  plan.highlighted
                    ? "border-blue-500 ring-2 ring-blue-500 ring-offset-2"
                    : "border-blue-100"
                }`}
              >
                {plan.highlighted && (
                  <span className="mb-2 inline-block text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">
                    Most Popular
                  </span>
                )}
                <h2 className="font-display text-xl text-gray-900">
                  {plan.name}
                </h2>
                <p className="mt-1 text-sm text-gray-600 leading-relaxed">{plan.description}</p>
                {plan.bestFor && (
                  <p className="mt-2 text-xs text-blue-600 font-medium italic">
                    Best for: {plan.bestFor}
                  </p>
                )}
                <p className="mt-4 text-2xl font-semibold text-blue-700" aria-label={`Price: ${plan.price}`}>
                  {plan.price}
                </p>
                <ul className="mt-4 flex-1 space-y-3 text-sm text-gray-700" role="list">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2" role="listitem">
                      <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span className="leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  <CTAButton
                    href="/programs"
                    variant={plan.highlighted ? "primary" : "outline"}
                    className="w-full justify-center text-sm"
                    ariaLabel={`Get started with ${plan.name} plan`}
                  >
                    Get Started
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </CTAButton>
                </div>
                <p className="mt-4 text-xs text-gray-500 leading-relaxed">
                  Book directly with trainers and nutritionists in the FitEasy
                  app. Taxes and city-specific rates may apply.
                </p>
              </article>
            ))}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}

